/**
 * Maria Bonita — motivos gráficos do sertão.
 *
 * Desenhados de raiz em SVG a partir da linguagem visual que a própria casa
 * usa nos seus posts (chapéu de cangaceiro com estrelas, cactos/mandacarus,
 * bandeiras Brasil + Portugal). Vetor = nítido em qualquer ecrã e leve.
 */

export function ChapeuCangaceiro({
  className,
  ink = "#241A12",
  star = "#F2C230",
}: {
  className?: string
  ink?: string
  star?: string
}) {
  return (
    <svg viewBox="0 0 128 78" className={className} fill="none" aria-hidden focusable="false">
      {/* aba levantada do chapéu */}
      <path
        d="M4 66C7 26 36 4 64 4s57 22 60 62c-14-16-33-25-60-25S18 50 4 66Z"
        fill={ink}
      />
      {/* correia do queixo */}
      <path
        d="M18 60c10 12 26 18 46 18s36-6 46-18"
        stroke={ink}
        strokeWidth="3.5"
        strokeLinecap="round"
        opacity="0.55"
      />
      {/* borlas laterais */}
      <circle cx="10" cy="70" r="4.5" fill={ink} />
      <circle cx="119" cy="70" r="4.5" fill={ink} />
      {/* estrelas — piscam desencontradas */}
      <Estrela cx={34} cy={44} r={8} fill={star} delay="0s" />
      <Estrela cx={64} cy={28} r={9.5} fill={star} delay="1.1s" />
      <Estrela cx={94} cy={44} r={8} fill={star} delay="2.2s" />
    </svg>
  )
}

function Estrela({
  cx,
  cy,
  r,
  fill,
  delay,
}: {
  cx: number
  cy: number
  r: number
  fill: string
  delay?: string
}) {
  const pts: string[] = []
  for (let i = 0; i < 10; i++) {
    const raio = i % 2 === 0 ? r : r * 0.42
    const ang = (Math.PI / 5) * i - Math.PI / 2
    pts.push(`${(cx + raio * Math.cos(ang)).toFixed(2)},${(cy + raio * Math.sin(ang)).toFixed(2)}`)
  }
  return (
    <polygon
      points={pts.join(" ")}
      fill={fill}
      className="bonita-estrela"
      style={{ "--bonita-brilho-delay": delay ?? "0s" } as React.CSSProperties}
    />
  )
}

/** Um mandacaru. `variante` muda o desenho para a fila não parecer copy-paste. */
export function Cacto({
  className,
  style,
  variante = 0,
  color = "#241A12",
}: {
  className?: string
  style?: React.CSSProperties
  variante?: 0 | 1 | 2 | 3
  color?: string
}) {
  const bracos = [
    "M18 44c-8 0-11-6-11-13M42 38c8 0 11-7 11-15",
    "M18 40c-8 0-11-7-11-16",
    "M42 46c8 0 11-7 11-14",
    "M18 36c-8 0-11-6-11-14M42 50c8 0 11-6 11-13",
  ][variante]

  return (
    <svg viewBox="0 0 60 80" className={className} style={style} fill="none" aria-hidden focusable="false">
      {/* tronco */}
      <path
        d="M30 79V22"
        stroke={color}
        strokeWidth="15"
        strokeLinecap="round"
      />
      {/* braços */}
      <path d={bracos} stroke={color} strokeWidth="9" strokeLinecap="round" />
      {/* espinhos */}
      <path
        d="M30 30h0M30 42h0M30 54h0"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.35"
      />
    </svg>
  )
}

/** Fila de cactos como separador de secção (o rodapé dos posts da casa). */
export function FilaCactos({
  className,
  color = "#241A12",
  opacity = 0.9,
}: {
  className?: string
  color?: string
  opacity?: number
}) {
  const alturas = [46, 62, 38, 54, 44, 66, 40, 58, 48, 36, 60, 42]
  return (
    <div
      className={`flex items-end justify-center gap-3 sm:gap-5 ${className ?? ""}`}
      style={{ opacity }}
      aria-hidden
    >
      {alturas.map((h, i) => (
        <Cacto
          key={i}
          variante={(i % 4) as 0 | 1 | 2 | 3}
          color={color}
          className="shrink-0 bonita-cacto"
          style={
            {
              height: h,
              width: (h * 60) / 80,
              // cada mandacaru baloiça no seu tempo — nunca em uníssono
              "--bonita-vento-delay": `${(i * 0.37).toFixed(2)}s`,
              "--bonita-vento-dur": `${(4.4 + (i % 5) * 0.6).toFixed(1)}s`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  )
}

/** Bandeirinhas Brasil + Portugal — estão no logótipo original da casa. */
export function Bandeiras({ className }: { className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${className ?? ""}`} aria-hidden>
      <svg viewBox="0 0 28 20" className="h-3.5 w-auto rounded-[2px]" focusable="false">
        <rect width="28" height="20" fill="#009B3A" />
        <polygon points="14,2.5 25.5,10 14,17.5 2.5,10" fill="#FEDF00" />
        <circle cx="14" cy="10" r="4.4" fill="#002776" />
      </svg>
      <svg viewBox="0 0 30 20" className="h-3.5 w-auto rounded-[2px]" focusable="false">
        <rect width="30" height="20" fill="#DA291C" />
        <rect width="12" height="20" fill="#046A38" />
        <circle cx="12" cy="10" r="4.6" fill="#FFE900" stroke="#DA291C" strokeWidth="0.8" />
        <circle cx="12" cy="10" r="2.7" fill="#fff" stroke="#002776" strokeWidth="1.3" />
      </svg>
    </span>
  )
}
